import { format, formatInTimeZone, utcToZonedTime } from 'date-fns-tz';

import { AnyThinEvent, getEventDisplayWithTime, getEventEndsAt, getEventStartsAt } from '@/utils/eventHelpers';

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 60 * 60;

function toTwoDigits(time) {
  return `0${time}`.slice(-2);
}

/**
 * Format given seconds to <hh>:mm:ss format.
 */
export function formatTime(seconds) {
  const time = [];
  const hours = Math.floor(seconds / SECONDS_IN_HOUR);

  if (hours > 0) {
    time.push(toTwoDigits(hours));
  }

  const minutes = Math.floor((seconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE);

  time.push(toTwoDigits(minutes));

  const secs = Math.floor((seconds % SECONDS_IN_HOUR) % SECONDS_IN_MINUTE);

  time.push(toTwoDigits(secs));

  return time.join(':');
}

export const dateToString = (date: string, dateFormat: string) => {
  if (!date) {
    return '';
  }

  return format(new Date(date), dateFormat);
};

export const formatConferenceDate = (
  event: AnyThinEvent,
  localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  // date-fns time format; the event's `time_format` where the configuration is at hand. The 12h
  // default is what every caller printed before the setting existed.
  timeFormat = 'h:mm a'
) => {
  const start = new Date(getEventStartsAt(event));
  const end = new Date(getEventEndsAt(event));

  if (getEventDisplayWithTime(event)) {
    return formatBaseDateWithDate(start, end, localTimezone, timeFormat);
  } else {
    return formatBaseDate(start, end, localTimezone);
  }
};

function formatBaseDate(startDate: Date, endDate: Date, timezone: string) {
  const longDash = String.fromCharCode(8212);

  if (isSameDayInTimezone(endDate, startDate, timezone)) {
    return `${formatInTimeZone(endDate, timezone, 'd MMMM yyyy')}`;
  }

  if (!isSameYearInTimezone(endDate, startDate, timezone)) {
    return `${formatInTimeZone(startDate, timezone, 'd MMMM yyyy')} ${longDash} ${formatInTimeZone(
      endDate,
      timezone,
      'd MMMM yyyy'
    )}`;
  }

  if (!isSameMonthInTimezone(endDate, startDate, timezone)) {
    return `${formatInTimeZone(startDate, timezone, 'd MMMM')} ${longDash} ${formatInTimeZone(
      endDate,
      timezone,
      'd MMMM yyyy'
    )}`;
  }

  return `${formatInTimeZone(startDate, timezone, 'd')} ${longDash} ${formatInTimeZone(
    endDate,
    timezone,
    'd MMMM yyyy'
  )}`;
}

function formatBaseDateWithDate(startDate: Date, endDate: Date, timezone: string, timeFormat: string) {
  const longDash = String.fromCharCode(8212);

  if (isSameDayInTimezone(endDate, startDate, timezone)) {
    return `${formatInTimeZone(startDate, timezone, `d MMMM yyyy ${timeFormat}`)}`;
  }

  if (!isSameYearInTimezone(endDate, startDate, timezone)) {
    return `${formatInTimeZone(startDate, timezone, `d MMMM yyyy ${timeFormat}`)} ${longDash} ${formatInTimeZone(
      endDate,
      timezone,
      `d MMMM yyyy ${timeFormat}`
    )}`;
  }

  if (!isSameMonthInTimezone(endDate, startDate, timezone)) {
    return `${formatInTimeZone(startDate, timezone, `d MMMM ${timeFormat}`)} ${longDash} ${formatInTimeZone(
      endDate,
      timezone,
      `d MMMM yyyy ${timeFormat}`
    )}`;
  }

  return `${formatInTimeZone(startDate, timezone, `d MMMM ${timeFormat}`)} ${longDash} ${formatInTimeZone(
    endDate,
    timezone,
    `d MMMM yyyy ${timeFormat}`
  )}`;
}

function getPartsInTimezone(date: Date, timeZone: string) {
  const zoned = utcToZonedTime(date, timeZone);

  return {
    year: format(zoned, 'yyyy', { timeZone }),
    month: format(zoned, 'MM', { timeZone }),
    day: format(zoned, 'dd', { timeZone }),
  };
}

function isSameDayInTimezone(date1: Date, date2: Date, timeZone: string) {
  const a = getPartsInTimezone(date1, timeZone);
  const b = getPartsInTimezone(date2, timeZone);

  return a.year === b.year && a.month === b.month && a.day === b.day;
}

function isSameMonthInTimezone(date1: Date, date2: Date, timeZone: string) {
  const a = getPartsInTimezone(date1, timeZone);
  const b = getPartsInTimezone(date2, timeZone);

  return a.year === b.year && a.month === b.month;
}

function isSameYearInTimezone(date1: Date, date2: Date, timeZone: string) {
  const a = getPartsInTimezone(date1, timeZone);
  const b = getPartsInTimezone(date2, timeZone);

  return a.year === b.year;
}
